import React from 'react';
import { Check, Edit2, Trash2, Upload, Loader2, Wand2, RefreshCw, X, Search } from 'lucide-react';
import { DraftLevel, DraftStatus } from '../../studio/types';
import { LEVEL_TYPE_LABELS } from '../../studio/planner';
import { usesPicture } from '../../studio/pipeline';
import { canApprove, wordSearchProblem } from '../../studio/publish';
import { isCategoryType } from '../../studio/rules';
import { gridLetters } from '../../studio/gridBuilder';
import { pastedImageFile } from '../../utils/imageUpload';
import { canFindPicture } from '../../studio/imageSearch';
import { JobState } from '../../studio/jobQueue';
import { hasFactWarning, isWebAddress, sourceLabel } from '../../studio/funFact';
import { LevelType, WordSearchGrid } from '../../types/Level';

const STORAGE_BASE = 'https://firebasestorage.googleapis.com/v0/b/thetower-2d922.firebasestorage.app/o/';
const imageUrl = (filename: string) => `${STORAGE_BASE}${encodeURIComponent(filename)}?alt=media`;

const STATUS_LABELS: Record<DraftStatus, string> = {
  ready: 'Ready',
  'needs-picture': 'Needs picture',
  'needs-grid': 'Needs grid',
  failed: 'Failed',
};

const VECTORS: Record<string, [number, number]> = {
  'horizontal-reverse': [0, -1],
  'vertical': [1, 0],
  'vertical-up': [-1, 0],
  'diagonal-down-left': [1, -1],
  'diagonal-up-left': [-1, -1],
};

// The cells that belong to a placed word, as "row-column" keys
const wordCells = (grid: WordSearchGrid): Set<string> => {
  const cells = new Set<string>();
  grid.wordPositions.forEach(position => {
    const [dRow, dCol] = VECTORS[position.direction] || [0, -1];
    for (let i = 0; i < gridLetters(position.word).length; i++) {
      cells.add(`${position.startX + dRow * i}-${position.startY + dCol * i}`);
    }
  });
  return cells;
};

interface DraftCardProps {
  draft: DraftLevel;
  // 'approve' while approving, otherwise the text of what is happening, or null when idle
  busy: string | null;
  // A picture job for this card is waiting its turn; the card stays usable and the job can be cancelled
  waiting: boolean;
  error: string | null;
  onApprove: () => void;
  onEdit: () => void;
  onReject: () => void;
  onPicture: (file: File) => void;
  onFindPicture: () => void;
  onGeneratePicture: () => void;
  onCancelPicture: () => void;
  onRebuildGrid: () => void;
  // Whether this card's fun fact job is waiting its turn or being written
  factState: JobState;
  factError: string | null;
  onWriteFact: () => void;
  onCancelFact: () => void;
  onRemoveFact: () => void;
}

const DraftCard: React.FC<DraftCardProps> = ({
  draft, busy, waiting, error, onApprove, onEdit, onReject, onPicture, onFindPicture, onGeneratePicture, onCancelPicture, onRebuildGrid,
  factState, factError, onWriteFact, onCancelFact, onRemoveFact,
}) => {
  const showPicture = usesPicture(draft.levelType);
  const isBusy = busy !== null;
  // A fact being written will be saved onto this card, so the card must not leave the queue meanwhile
  const locked = isBusy || factState === 'running';
  const factSource = draft.funFactSource && isWebAddress(draft.funFactSource) ? draft.funFactSource : null;
  const grid = draft.wordSearchGrid;
  const highlighted = grid ? wordCells(grid) : new Set<string>();

  const handlePaste = (event: React.ClipboardEvent) => {
    if (!showPicture || isBusy) return;
    const file = pastedImageFile(event.clipboardData.items);
    if (!file) return;
    event.preventDefault();
    onPicture(file);
  };

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) onPicture(file);
  };

  return (
    <div className="draft-card" tabIndex={0} onPaste={handlePaste}>
      <div className="draft-card-top">
        <span className="draft-type">{LEVEL_TYPE_LABELS[draft.levelType]}</span>
        <span className={`draft-status ${draft.status}`}>{STATUS_LABELS[draft.status]}</span>
      </div>

      {isCategoryType(draft.levelType) ? (
        <div className="draft-answer">{draft.category}</div>
      ) : draft.levelType === LevelType.WhereIsThisLocated ? (
        <>
          <div className="draft-answer">{draft.locationName}</div>
          <div className="draft-sub">{draft.sentence}</div>
        </>
      ) : (
        <div className="draft-answer">{draft.sentence}</div>
      )}

      {draft.context && <div className="draft-sub">{draft.context}</div>}

      {draft.words && draft.words.length > 0 && (
        <div className="draft-words">
          {draft.words.map((word, index) => <span key={`${word}-${index}`} className="draft-word">{word}</span>)}
        </div>
      )}

      {grid && (
        <div className="draft-grid">
          {grid.grid.map((row, rowIndex) => row.map((letter, colIndex) => (
            <span key={`${rowIndex}-${colIndex}`} className={highlighted.has(`${rowIndex}-${colIndex}`) ? 'in-word' : ''}>
              {letter}
            </span>
          )))}
        </div>
      )}

      {draft.levelType === LevelType.WordSearch && (
        <div className="draft-picture-buttons">
          <button className="btn btn-secondary btn-small" onClick={onRebuildGrid} disabled={isBusy}>
            <RefreshCw size={14} /> {grid ? 'Rebuild grid' : 'Build grid'}
          </button>
        </div>
      )}

      {draft.image && (
        <img
          className="draft-image"
          src={imageUrl(draft.image) + (draft.imageSize ? `&v=${draft.imageSize}` : '')}
          alt=""
        />
      )}

      {draft.image && draft.imageCredit && <div className="draft-credit">Photo: {draft.imageCredit}</div>}

      {showPicture && (
        <div className="draft-picture-zone">
          <div className="draft-picture-buttons">
            {canFindPicture(draft.levelType) && (
              <button className="btn btn-primary btn-small" onClick={onFindPicture} disabled={isBusy}>
                <Search size={14} /> Find a picture
              </button>
            )}
            {waiting ? (
              <button className="btn btn-secondary btn-small" onClick={onCancelPicture}>
                <X size={14} /> Cancel waiting picture
              </button>
            ) : (
              <button className="btn btn-secondary btn-small" onClick={onGeneratePicture} disabled={isBusy}>
                <Wand2 size={14} /> {draft.image ? 'Regenerate picture' : 'Generate picture'}
              </button>
            )}
            <label className="btn btn-secondary btn-small" style={{ cursor: isBusy ? 'not-allowed' : 'pointer' }}>
              <Upload size={14} /> Upload
              <input type="file" accept="image/*" onChange={handleFile} disabled={isBusy} style={{ display: 'none' }} />
            </label>
          </div>
          <span>Or click this card and press Ctrl+V. Pasted and uploaded pictures can be cropped.</span>
        </div>
      )}

      {wordSearchProblem(draft) && <div className="draft-flag">{wordSearchProblem(draft)}</div>}

      {draft.status === 'needs-grid' && (
        <div className="draft-flag">The words could not be placed automatically. Try Build grid, or click Edit to place them by hand.</div>
      )}

      {draft.keyLetters && draft.keyLetters.length > 0 && (
        <div className="draft-letters">
          {draft.keyLetters.map(letter => <span key={letter} className="draft-letter">{letter}</span>)}
        </div>
      )}

      <div className="draft-meta">
        Difficulty {draft.difficulty}/5{draft.difficultyReason ? ` · ${draft.difficultyReason}` : ''}
      </div>

      <div className="draft-fact">
        <div className="draft-fact-title">Fun fact</div>
        {draft.funFact ? (
          <>
            <div className="draft-fact-text">{draft.funFact}</div>
            {factSource && (
              <div className="draft-fact-source">
                {!hasFactWarning(draft) && <span className="draft-badge"><Check size={12} /> <span>Verified</span></span>}
                <a href={factSource} target="_blank" rel="noopener noreferrer">{sourceLabel(factSource)}</a>
              </div>
            )}
          </>
        ) : (
          <div className="draft-fact-none">No fun fact yet.</div>
        )}
        <div className="draft-fact-buttons">
          {factState === 'waiting' ? (
            <button className="btn btn-secondary btn-small" onClick={onCancelFact}>
              <X size={14} /> Cancel waiting fact
            </button>
          ) : (
            <button className="btn btn-secondary btn-small" onClick={onWriteFact} disabled={locked}>
              <Wand2 size={14} /> {draft.funFact ? 'Redo fact' : 'Write fun fact'}
            </button>
          )}
          {draft.funFact && (
            <button className="btn btn-secondary btn-small" onClick={onRemoveFact} disabled={locked}>
              <Trash2 size={14} /> Remove fact
            </button>
          )}
        </div>
        {factState === 'waiting' && <div className="draft-busy">Waiting for its turn to get a fun fact...</div>}
        {factState === 'running' && (
          <div className="draft-busy"><Loader2 className="animate-spin" size={14} /> Finding and checking a fun fact...</div>
        )}
        {factError && <div className="studio-error">{factError}</div>}
      </div>

      {draft.flags.map(flag => <div key={flag} className="draft-flag">{flag}</div>)}
      {error && <div className="studio-error">{error}</div>}
      {waiting && !busy && <div className="draft-busy">Waiting for its turn to get a picture...</div>}
      {busy && busy !== 'approve' && (
        <div className="draft-busy"><Loader2 className="animate-spin" size={14} /> {busy}</div>
      )}

      <div className="draft-actions">
        <button className="btn btn-primary btn-small" onClick={onApprove} disabled={locked || !canApprove(draft)}>
          {busy === 'approve' ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Approve
        </button>
        <button className="btn btn-secondary btn-small" onClick={onEdit} disabled={locked}>
          <Edit2 size={14} /> Edit
        </button>
        <button className="btn btn-secondary btn-small" onClick={onReject} disabled={locked}>
          <Trash2 size={14} /> Reject
        </button>
      </div>
    </div>
  );
};

export default DraftCard;
